import { useId, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import type {
  StorefrontDestination,
  SupabaseClient,
  WebsiteDestinationKind,
} from '@dukanti/shared';
import { destinationSummaryKey } from '@dukanti/shared/admin';
import { useTranslation, type TranslationKey } from '@dukanti/shared/i18n';
import { CategoryDestinationPicker } from './category-destination-picker';
import { DestinationKindCards } from './destination-kind-cards';
import {
  cardForValue,
  destinationCardsFor,
  isIncompletePick,
  isMoreCard,
  pickedTargetId,
  valueForCard,
  type DestinationCardId,
} from './destination-model';
import { CollectionSelect, ContactMethodSelect, StorePageSelect } from './destination-sub-selects';
import { ExternalUrlField } from './external-url-field';
import { externalUrlIssueKey } from './external-url-issue';
import type { PickerPromptTone } from './picker-parts';
import { ProductDestinationPicker } from './product-destination-picker';

export interface DestinationEditorProps {
  /** Injected client (no singleton import): the picker hooks run on it. */
  readonly client: SupabaseClient;
  /** The picker hooks build image URLs from it. */
  readonly supabaseUrl: string;
  readonly storeId: string;
  readonly value: StorefrontDestination;
  /** The kinds the slot accepts (`websiteAllowedLinkKinds`); "No link" is always offered. */
  readonly allowedKinds: readonly WebsiteDestinationKind[];
  /**
   * Every choice the merchant makes. A card with nothing picked yet, or a
   * URL that is empty or refused, arrives incomplete; the consumer validates
   * before it saves (RULE 0), so the saved value stays until a complete one
   * replaces it.
   */
  readonly onChange: (next: StorefrontDestination) => void;
  /** PD10: the stored category/product is gone (readiness `invalid_reference`). The id is kept. */
  readonly unavailable?: boolean;
  /** PD10: the name of the stored pick, from the store's own lists. */
  readonly selectedLabel?: string | null;
  /** Admin locale key of a message for the field (RULE 0 or the server). */
  readonly issueKey?: string;
  readonly label: string;
  readonly testIdPrefix?: string;
  /**
   * Admin locale key of a neutral note under the cards while a destination
   * is set (not "No link"), e.g. the element has no button label to tap.
   */
  readonly noticeKey?: string;
  /**
   * The merchant tried to finish (Done, close, leave) while the value is
   * incomplete: the pick prompt turns from a neutral hint into an error and
   * a refused URL shows its reason at once (UI/UX P2-2).
   */
  readonly finishRequested?: boolean;
}

/** The generic "choose where this link goes" of a refused link value (`destinationIssueKey`). */
const DESTINATION_INVALID_ISSUE = 'admin.destination.invalid';
const EXTERNAL_REFUSED_ISSUE = 'admin.destination.externalRefused';

/**
 * The shared destination editor of clickable elements (PRD §7, PD9): "No
 * link", "Sales page", "Category", "Product", "Instagram or Facebook", then
 * "More destinations" for the other store pages, a collection and contact.
 * It offers only the kinds the slot allows and never navigates anywhere.
 */
export function DestinationEditor({
  client,
  supabaseUrl,
  storeId,
  value,
  allowedKinds,
  onChange,
  unavailable = false,
  selectedLabel = null,
  issueKey,
  label,
  testIdPrefix = 'destination',
  noticeKey,
  finishRequested = false,
}: DestinationEditorProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const id = useId();
  const labelId = `${id}-label`;
  const messageId = `${id}-message`;
  const cards = destinationCardsFor(allowedKinds);
  const selected = cardForValue(value);
  const [moreToggled, setMoreToggled] = useState(false);
  // A stored value behind the disclosure keeps it open (PD9).
  const moreOpen = moreToggled || isMoreCard(selected);
  // What each card held last in this session, so switching back restores it.
  const remembered = useRef(new Map<DestinationCardId, StorefrontDestination>());

  const select = (card: DestinationCardId): void => {
    remembered.current.set(selected, value);
    // A primary choice folds "More destinations", so its picker sits under the cards (UI/UX P3-6).
    if (!isMoreCard(card)) setMoreToggled(false);
    const restored = remembered.current.get(card);
    onChange(restored ?? valueForCard(card));
  };

  // The URL field shows its own reasons; an empty pick is prompted inside its picker,
  // never as a red line under the cards, an empty state or a load error (UI/UX P2-2).
  const fieldIssue: string | undefined =
    value.kind === 'external' || isIncompletePick(value) ? undefined : issueKey;
  const promptTone: PickerPromptTone = finishRequested ? 'error' : 'neutral';
  // A URL the client accepts but the server refused says so plainly (UI/UX P3-2).
  const urlServerIssue = issueKey === DESTINATION_INVALID_ISSUE ? EXTERNAL_REFUSED_ISSUE : issueKey;
  const pickUnavailable = unavailable && pickedTargetId(value) !== '';

  return (
    <section dir={dir} className="space-y-3" data-testid={testIdPrefix}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p id={labelId} className="text-foreground text-start text-sm font-medium">
          {label}
        </p>
        <Summary
          value={value}
          unavailable={pickUnavailable}
          selectedLabel={selectedLabel}
          testId={`${testIdPrefix}-summary`}
        />
      </div>

      <DestinationKindCards
        primary={cards.primary}
        more={cards.more}
        selected={selected}
        moreOpen={moreOpen}
        onMoreOpenChange={setMoreToggled}
        onSelect={select}
        label={label}
        describedBy={fieldIssue === undefined ? undefined : messageId}
        testIdPrefix={testIdPrefix}
      />

      {noticeKey !== undefined && value.kind !== 'none' ? (
        <p
          className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-start text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-200"
          data-testid={`${testIdPrefix}-notice`}
        >
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{t(noticeKey as TranslationKey)}</span>
        </p>
      ) : null}

      {value.kind === 'category' ? (
        <CategoryDestinationPicker
          client={client}
          supabaseUrl={supabaseUrl}
          storeId={storeId}
          selectedId={value.categoryId}
          selectedLabel={selectedLabel}
          unavailable={pickUnavailable}
          promptTone={promptTone}
          onSelect={(categoryId) => onChange({ kind: 'category', categoryId })}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {value.kind === 'product' ? (
        <ProductDestinationPicker
          client={client}
          supabaseUrl={supabaseUrl}
          storeId={storeId}
          selectedId={value.productId}
          selectedLabel={selectedLabel}
          unavailable={pickUnavailable}
          promptTone={promptTone}
          onSelect={(productId) => onChange({ kind: 'product', productId })}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {value.kind === 'external' ? (
        <ExternalUrlField
          url={value.url}
          serverIssueKey={urlServerIssue}
          forceReveal={finishRequested}
          onDraftChange={(url) => onChange({ kind: 'external', url })}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {value.kind === 'store-page' && value.page !== 'sales' ? (
        <StorePageSelect
          page={value.page}
          onChange={(page) => onChange({ kind: 'store-page', page })}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {value.kind === 'contact' ? (
        <ContactMethodSelect
          method={value.method}
          onChange={(method) => onChange({ kind: 'contact', method })}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      {value.kind === 'custom-collection' ? (
        <CollectionSelect
          client={client}
          storeId={storeId}
          collectionRef={value.collectionRef}
          unavailable={pickUnavailable}
          promptTone={promptTone}
          onChange={(collectionRef) => onChange({ kind: 'custom-collection', collectionRef })}
          testIdPrefix={testIdPrefix}
        />
      ) : null}

      {fieldIssue === undefined ? null : (
        <p
          id={messageId}
          role="alert"
          className="text-destructive text-start text-xs"
          data-testid={`${testIdPrefix}-error`}
        >
          {t(fieldIssue as TranslationKey)}
        </p>
      )}
    </section>
  );
}

function Summary({
  value,
  unavailable,
  selectedLabel,
  testId,
}: {
  readonly value: StorefrontDestination;
  readonly unavailable: boolean;
  readonly selectedLabel: string | null;
  readonly testId: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  // Nothing is chosen yet, or the typed URL is refused: the summary names only what can be saved.
  if (isIncompletePick(value)) return null;
  if (value.kind === 'external' && externalUrlIssueKey(value.url) !== null) return null;
  const text = unavailable
    ? t('admin.destination.summary.unavailable')
    : t(destinationSummaryKey(value) as TranslationKey, {
        value:
          value.kind === 'external'
            ? value.url
            : (selectedLabel ?? t('admin.destination.selectedUnknown')),
        // React escapes text; i18next escaping would show `&#x2F;` in a URL or `&amp;` in a name.
        interpolation: { escapeValue: false },
      });
  return (
    <p
      className="text-muted-foreground min-w-0 truncate text-start text-xs"
      dir="auto"
      data-testid={testId}
    >
      {text}
    </p>
  );
}
